// powerEfficiencySeries.ts

import Highcharts from 'highcharts';

import type { TimeseriesMetric } from '@/pcs/domain/entities/pcs-timeseries';

import { baseColors } from '../../color-palette';
import { generateShade } from '../../color-utils';

export function generatePowerEfficiencySeries(section: TimeseriesMetric): {
  series: Highcharts.SeriesOptionsType[];
  yAxisMin: number; // We'll return the dynamic min
} {
  // 1) Determine color mappings
  const uniqueIds = Array.from(new Set(section.lines.map((line) => line.id)));

  const idToFrontendID: Record<string, string> = {};
  section.lines.forEach((line) => {
    if (!idToFrontendID[line.id]) {
      idToFrontendID[line.id] = line.fullFrontendID;
    }
  });

  const idColorMap: Record<string, string> = {};
  uniqueIds.forEach((id, index) => {
    idColorMap[id] = baseColors[index % baseColors.length];
  });

  // 2) Filter out lines that are always 100% OK
  const filteredLines = section.lines.filter((line) => {
    const okSubLine = line.subLines.find((s) => s.label === 'ok');
    if (!okSubLine) return true;
    return !okSubLine.data.every((point) => point.value === 100);
  });

  // 3) Build the series array
  const series: Highcharts.SeriesOptionsType[] = [];

  filteredLines.forEach((line) => {
    // Generate color variants
    const darkerColor = generateShade(idColorMap[line.id], 'gap');
    const lighterColor = generateShade(idColorMap[line.id], 'invalid');

    // We'll look for subLines: avg, min, max
    const avgSubLine = line.subLines.find((s) => s.label === 'avg');
    const minSubLine = line.subLines.find((s) => s.label === 'min');
    const maxSubLine = line.subLines.find((s) => s.label === 'max');

    // 3a) Range (min/max) — fall back through avg when either bound is null
    if (minSubLine && maxSubLine) {
      series.push({
        type: 'arearange',
        name: `${line.fullFrontendID} range (${line.idStatus})`,
        data: minSubLine.data.map((point, idx) => {
          const ts = point.timestamp.getTime();
          const minVal = point.value;
          const maxVal = maxSubLine.data[idx].value;
          const avgVal = avgSubLine?.data[idx]?.value ?? null;
          let lower = minVal ?? avgVal ?? maxVal;
          let upper = maxVal ?? avgVal ?? minVal;
          // Guard against all-null scenario
          if (lower == null || upper == null) return [ts, null, null];
          // Ensure valid range (defensive against anomalous data)
          if (lower > upper) [lower, upper] = [upper, lower];
          return [ts, lower, upper];
        }),
        color: lighterColor,
        fillOpacity: 0.2,
        lineWidth: 0,
        showInLegend: false,
      });
    }

    // 3b) Avg line — fill gaps with min/max fallback for continuity
    if (avgSubLine) {
      series.push({
        type: 'line',
        name: idToFrontendID[line.id],
        data: avgSubLine.data.map((p, idx) => {
          const val =
            p.value ??
            minSubLine?.data[idx]?.value ??
            maxSubLine?.data[idx]?.value;
          return [p.timestamp.getTime(), val];
        }),
        color: darkerColor,
        showInLegend: false,
        zIndex: 2,
        marker: {
          enabled: true,
          symbol: 'circle',
          radius: 2,
        },
      });
    }

    // 3c) Dedicated min line for single-PCS view (avoids fleet clutter)
    if (uniqueIds.length === 1 && minSubLine) {
      series.push({
        type: 'line',
        name: `${idToFrontendID[line.id]} min`,
        data: minSubLine.data.map((p) => [
          p.timestamp.getTime(),
          p.value ?? null, // Explicit null for Highcharts gap handling
        ]),
        color: darkerColor,
        dashStyle: 'ShortDash',
        lineWidth: 1,
        showInLegend: false,
        zIndex: 1,
        marker: { enabled: false },
      });
    }
  });

  // 4) Compute dynamic min from all data values
  const allValues = series
    .flatMap((s) =>
      'data' in s && Array.isArray(s.data)
        ? s.data.map((d) => {
            // For arearange, d might be [x, low, high]
            if (Array.isArray(d) && d.length > 2) {
              return d[1]; // the "low" for a range
            }
            // For a line, d might be [x, y]
            if (Array.isArray(d) && d.length > 1) {
              return d[1];
            }
            return null;
          })
        : [],
    )
    .filter((v) => v !== null) as number[];

  let computedMin = 90;
  if (allValues.length > 0) {
    const minValue = Math.min(...allValues);
    // If the min is below 90, use that; otherwise stick to 90
    computedMin = minValue < 90 ? minValue : 90;
  }

  return { series, yAxisMin: computedMin };
}
